import { useTranslate } from '@ur-solutions/react-hooks'
import { SidebarNavItem } from '@/modules/sidebar'
import UserThumbOrInitials from '@/modules/users/UserThumbOrInitials'
import React, { useState } from 'react'
import { isMobile } from 'react-device-detect'
import styled, { css } from 'styled-components'
import { useUser } from '@/utils/hooks'
import { MobileUserMenu } from './MobileUserMenu'
import { useSidebarConfig } from './sidebar-config'
import { useIsTabletSize } from '../../utils/hooks/useIsTabletSize'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  .time-entries svg {
    box-sizing: content-box;
    padding: 0 3px;
  }
  .settings svg {
    box-sizing: content-box;
    padding: 0 3px;
  }
`

interface SeparatorProps {
  $tall?: boolean
}
const Separator = styled.div<SeparatorProps>`
  width: 100%;
  margin-top: 1em;
  padding: 2em 16px 1em;

  font-size: 0.8em;
  font-weight: 600;
  color: ${props => props.theme.colors.gray3};

  border-top: 1px solid ${props => props.theme.colors.gray8};

  ${props => props.theme.layout.mobile} {
    ${props =>
      props.$tall &&
      css`
        margin-top: 2em;
      `};
  }
`
const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.6em;
  padding: 0.6em 16px 1em;

  border-bottom: 1px solid ${props => props.theme.colors.gray8};

  h2 {
    margin: 0;
    font-weight: 600;
  }
  & > div {
    position: relative;
    display: grid;
    grid-auto-flow: column;
    gap: 1em;
  }
`

export const SidebarNav: React.FC = () => {
  const translations = useTranslate({
    labelGeneral: 'common.general',
    labelDocumentation: 'common.documentation',
    labelInformation: 'common.information',

    remainingDrivingTime: 'common.remaining-driving-time',

    menu: 'common.menu',

    dashboard: 'common.dashboard',
    users: 'common.users',
    forum: 'common.forum',
    drivingData: 'common.driving-data',
    issues: 'common.issues',
    infringements: 'common.infringements',
    timeEntries: 'common.hourlist',
    absences: 'common.absence',
    handbook: 'common.driversHandbook',
    formCreator: 'common.formcreator',
    companySettings: 'settings.company-settings',
    vehicleFleet: 'vehicles.fleet-overview',
    fleet: 'vehicles.fleet',
    driverPlanner: 'common.driver-planner',
    fleetManagement: 'vehicles.fleet-management',
    routePlanner: 'common.route-planner',
    userExports: 'exports.user-exports',
    salaryBases: 'salary.salary-bases',
    workTimeAndSalary: 'salary.work-time-and-salary',
    checklists: 'common.checklists',
    exportAndSettings: 'common.export-and-settings',
  })

  const user = useUser()
  const { isTablet } = useIsTabletSize()
  const isCompactView = isMobile || isTablet

  const [mobileUserMenuOpen, setMobileUserMenuOpen] = useState(false)

  const config = useSidebarConfig()

  return (
    <Wrapper>
      {isCompactView && (
        <>
          <Head>
            <h2>{translations.menu}</h2>

            <div>
              <UserThumbOrInitials
                user={user}
                size={42}
                color="primary"
                backgroundColor="secondary"
                fontSize="0.7em"
                fontWeight="600"
                onClick={() => setMobileUserMenuOpen(isCompactView)}
              />

              {isCompactView && (
                <MobileUserMenu
                  open={mobileUserMenuOpen}
                  user={user}
                  onClose={() => setMobileUserMenuOpen(false)}
                />
              )}
            </div>
          </Head>
        </>
      )}

      {config.map(item => {
        if (item.type === 'item')
          return (
            <SidebarNavItem
              key={item.to}
              className={item.className}
              to={item.to}
              end={item.end}
              target={item.target}
              text={item.text}
              icon={item.icon}
              iconWidth={item.iconWidth}
              marginOverride={item.marginOverride}
              badge={item.badge}
              badgeLoading={item.badgeLoading}
              onBeforeNavigate={item.onBeforeNavigate}
            />
          )

        return (
          <React.Fragment key={item.header}>
            <Separator $tall={item.tall}>{item.header}</Separator>

            {item.items.map(subItem => (
              <SidebarNavItem
                key={subItem.to}
                className={subItem.className}
                to={subItem.to}
                end={subItem.end}
                target={subItem.target}
                text={subItem.text}
                icon={subItem.icon}
                iconWidth={subItem.iconWidth}
                marginOverride={subItem.marginOverride}
                badge={subItem.badge}
                badgeLoading={subItem.badgeLoading}
                onBeforeNavigate={subItem.onBeforeNavigate}
              />
            ))}
          </React.Fragment>
        )
      })}
    </Wrapper>
  )
}
